import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetArticlesQuery, useDeleteArticleMutation, useToggleArticleStatusMutation, useToggleArticleFeaturedMutation, useToggleArticlePremiumMutation, useMassActionMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import Pagination from '../../../components/Pagination';
import { confirmDelete } from '../../../utils/confirm';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

// Format date as "d M, Y" (e.g., "08 Jun, 2024")
const formatDateLaravel = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminArticles: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const { data, isLoading, error, refetch } = useGetArticlesQuery({ search, daterange, page });
  const [deleteArticle] = useDeleteArticleMutation();
  const [toggleArticleStatus] = useToggleArticleStatusMutation();
  const [toggleArticleFeatured] = useToggleArticleFeaturedMutation();
  const [toggleArticlePremium] = useToggleArticlePremiumMutation();
  const [massAction] = useMassActionMutation();

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteArticle(id).unwrap();
      showSuccess(result.message || t('Article deleted successfully!'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to delete article. Please try again.'));
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const result = await toggleArticleStatus({ id, status: newStatus }).unwrap();
      showSuccess(result.message || t('Article status updated successfully!'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update article status. Please try again.'));
    }
  };

  const handleToggleFeatured = async (id: number, currentFeatured: number) => {
    try {
      const newFeatured = currentFeatured === 1 ? 0 : 1;
      const result = await toggleArticleFeatured({ id, is_featured: newFeatured }).unwrap();
      showSuccess(result.message || t('Featured status updated successfully!'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update featured status. Please try again.'));
    }
  };

  const handleTogglePremium = async (id: number, currentPremium: number) => {
    try {
      const newPremium = currentPremium === 1 ? 0 : 1;
      const result = await toggleArticlePremium({ id, is_premium: newPremium }).unwrap();
      showSuccess(result.message || t('Premium status updated successfully!'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update premium status. Please try again.'));
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allIds = articles.data.map((article: any) => article.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
      setSelectAll(false);
    }
  };

  const handleBulkAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkAction || selectedIds.length === 0) {
      showError(t('Please select an action and at least one article'));
      return;
    }

    if (bulkAction === 'delete') {
      const confirmed = await confirmDelete(t('Are you sure?'), t("You won't be able to revert this!"));
      if (!confirmed) return;
    }

    try {
      const result = await massAction({
        type: 'article',
        action: bulkAction,
        selectedIds: selectedIds.join(','),
      }).unwrap();
      showSuccess(result.message || t('Bulk action completed successfully!'));
      setSelectedIds([]);
      setSelectAll(false);
      setBulkAction('');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to perform bulk action. Please try again.'));
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">{t('Error loading articles')}</div>;
  }

  const articles = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Main Articles')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Home')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{t('Main Articles')}</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <a href="#" className="btn btn-base btn-sm" onClick={(e) => { e.preventDefault(); navigate('/admin/article-tags'); }}>
                {t('Tags List')}
              </a>
              <a href="#" className="btn btn-base btn-sm" onClick={(e) => { e.preventDefault(); navigate('/admin/articles/add'); }}>
                {t('Add New Article')}
              </a>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder={t('Search Article Title..')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <button type="submit" className="btn w-120px btn-base mx-3">{t('Filter')}</button>
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">
                            {t('Clear')}
                          </a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="card-body pt-0">
                <form onSubmit={handleBulkAction} className="d-flex mb-3">
                  <select
                    name="action"
                    required
                    className="form-control w-80px p-2 me-2"
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                  >
                    <option value="">{t('Action')}</option>
                    <option value="delete">{t('Delete')}</option>
                  </select>
                  <button className="btn btn-base btn-sm p-3" type="submit">{t('Confirm')}</button>
                </form>
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="w-10px pe-2">
                          <input
                            className="form-check-input border-base mt-2"
                            type="checkbox"
                            id="checkAll"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>#</th>
                        <th>{t('Image')}</th>
                        <th>{t('Title')}</th>
                        <th>{t('Tags')}</th>
                        <th>{t('Is Featured')}</th>
                        <th>{t('Is Premium')}</th>
                        <th>{t('Created Date')}</th>
                        <th>{t('Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {articles.data.map((article: any, index: number) => (
                        <tr key={article.id}>
                          <td>
                            <input
                              className="form-check-input border-base mt-2 action_ids"
                              type="checkbox"
                              value={article.id}
                              checked={selectedIds.includes(article.id)}
                              onChange={(e) => handleSelectItem(article.id, e.target.checked)}
                            />
                          </td>
                          <td>{(articles.current_page - 1) * 15 + index + 1}</td>
                          <td><img src={article.image || article.images?.[0] || '/media/blank.png'} width="60" className="rounded" alt="" /></td>
                          <td>{article.title}</td>
                          <td>
                            {article.tags && article.tags.length > 0 ? (
                              article.tags.map((tag: any) => (
                                <span key={tag.id} className="badge alert-dark m-1">
                                  {tag.tag || tag.name || tag.title}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <div className="form-check form-switch mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={article.is_featured === 1}
                                onChange={() => handleToggleFeatured(article.id, article.is_featured)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="form-check form-switch mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={article.is_premium === 1}
                                onChange={() => handleTogglePremium(article.id, article.is_premium)}
                              />
                            </div>
                          </td>
                          <td>{formatDateLaravel(article.created_at)}</td>
                          <td className="text-center d-flex">
                            <a
                              href="#"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/articles/${article.id}`); }}
                            >
                              <i className="fas fa-eye"></i>
                            </a>
                            <a
                              href="#"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/articles/${article.id}/edit`); }}
                            >
                              <i className="fas fa-pencil"></i>
                            </a>
                            <button
                              type="button"
                              className="delBtn btn btn-sm btn-light-primary"
                              onClick={() => handleDelete(article.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {articles.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={articles.current_page}
                      lastPage={articles.last_page}
                      total={articles.total || 0}
                      perPage={articles.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminArticles;
